// Elegir con qué rol entrar: una persona puede ser residente en un conjunto y domiciliaria en otro.

import { Link, Navigate, useSearchParams } from "react-router";
import { ROLES, type Conjunto, type EstadoMembresia, type Miembro, type Rol } from "@porteria/core";
import { useSesion } from "../sesion";
import { useDocumento } from "../datos";
import { AvisoInstalar, AvisoNotificaciones, Campana } from "../componentes";
import { Icono, type NombreIcono } from "../iconos";
import { Boton, Cargando, Etiqueta, LogoConjunto, Pantalla, Portada, Tarjeta, Vacio } from "../ui";

const ORDEN: Rol[] = ["RESIDENTE", "DOMICILIARIO", "VIGILANTE", "ADMIN"];
export const ICONOS_ROL: Record<Rol, NombreIcono> = { RESIDENTE: "casa", DOMICILIARIO: "moto", VIGILANTE: "escudo", ADMIN: "tablero" };

/** Logo del conjunto. Solo con un rol aprobado se puede leer el conjunto; si no, las iniciales del nombre. */
function LogoDe({ cid, nombre, aprobado }: { cid: string; nombre: string; aprobado: boolean }) {
  const conjunto = useDocumento<Conjunto>(aprobado ? `conjuntos/${cid}` : null);
  return <LogoConjunto nombre={nombre} url={conjunto?.logoUrl} />;
}

export const rutaDelRol = (cid: string, rol: Rol) =>
  rol === "ADMIN" ? `/admin/${cid}` : `/c/${cid}/${rol === "RESIDENTE" ? "residente" : rol === "DOMICILIARIO" ? "domiciliario" : "vigilante"}`;

const TONO: Record<EstadoMembresia, "ok" | "espera" | "alerta" | "neutro"> = {
  APROBADO: "ok",
  PENDIENTE: "espera",
  RECHAZADO: "alerta",
  DESHABILITADO: "alerta",
};

function roles(m: Miembro) {
  return ORDEN.filter((r) => m.roles?.[r]).map((r) => ({ rol: r, ...m.roles[r]! }));
}

export default function Inicio() {
  const { perfil, membresias, esSuper } = useSesion();
  const [parametros] = useSearchParams();
  // Sin saber aún si es dueño de la plataforma no se decide a dónde entrar directo.
  if (membresias === undefined || esSuper === undefined) return <Cargando />;

  const todos = membresias.flatMap((m) => roles(m).map((r) => ({ m, ...r })));
  const aprobados = todos.filter((r) => r.estado === "APROBADO");
  // Con un solo rol habilitado, entra directo (salvo que venga a cambiar de rol).
  if (aprobados.length === 1 && todos.length === 1 && !esSuper && !parametros.has("elegir")) {
    return <Navigate to={rutaDelRol(aprobados[0].m.conjuntoId, aprobados[0].rol)} replace />;
  }

  return (
    <Pantalla
      titulo={`Hola, ${perfil?.nombre.split(" ")[0] ?? ""}`}
      subtitulo="¿Cómo vas a usar la app?"
      acciones={
        <>
          <Campana />
          <Link to="/perfil" className="boton-icono" aria-label="Mi perfil">
            <Icono nombre="persona" />
          </Link>
        </>
      }
    >
      <Portada titulo="Tu portería, a la puerta de tu casa" icono="moto">
        <small>Elige con qué perfil quieres entrar.</small>
      </Portada>
      <AvisoNotificaciones motivo="Te avisamos cuando llegue un paquete, cuando lo recojan y cuando la administración apruebe tu cuenta." />

      {esSuper && (
        <Link to="/plataforma" className="tarjeta tocable" style={{ textDecoration: "none", color: "inherit" }}>
          <div className="elemento">
            <div className="icono-redondo tinta">
              <Icono nombre="ajustes" />
            </div>
            <div className="elemento-cuerpo">
              <strong>Panel de la plataforma</strong>
              <span className="chico texto-suave">Conjuntos, administradores, comisiones y liquidaciones</span>
            </div>
            <Icono nombre="adelante" className="flecha" />
          </div>
        </Link>
      )}

      {todos.length === 0 && (
        <Vacio icono="edificio" titulo="Aún no estás en ningún conjunto">
          Pide en la portería el código de tu conjunto (está en el afiche) y únete con tu apartamento. Si trabajas en la portería o llevas
          paquetes, la administración te crea la cuenta.
        </Vacio>
      )}

      {todos.map(({ m, rol, estado, motivo }) => {
        const detalle = rol === "RESIDENTE" ? `Torre ${m.roles.RESIDENTE!.torre} · Apto ${m.roles.RESIDENTE!.apto}` : null;
        const contenido = (
          <div className="elemento">
            <span style={{ position: "relative", flex: "none" }}>
              <LogoDe cid={m.conjuntoId} nombre={m.conjuntoNombre} aprobado={m.roles[rol]!.estado === "APROBADO"} />
              <span className="insignia-rol">
                <Icono nombre={ICONOS_ROL[rol]} tamano={14} />
              </span>
            </span>
            <div className="elemento-cuerpo">
              <strong>{ROLES[rol]}</strong>
              <span className="chico texto-suave">
                {m.conjuntoNombre}
                {detalle ? ` · ${detalle}` : ""}
              </span>
              {estado === "PENDIENTE" && (
                <span className="chico">
                  {rol === "RESIDENTE"
                    ? "Si alguien de tu apartamento ya usa la app, puede aprobarte desde ahí. Si no, la administración la revisa."
                    : "La administración está revisando tu solicitud."}
                </span>
              )}
              {(estado === "RECHAZADO" || estado === "DESHABILITADO") && motivo && <span className="chico">Motivo: {motivo}</span>}
              {rol === "DOMICILIARIO" && estado === "DESHABILITADO" && <span className="chico">Ábrelo para ver tu carné y tus cobros pendientes.</span>}
            </div>
            {estado === "APROBADO" ? <Icono nombre="adelante" className="flecha" /> : <Etiqueta tono={TONO[estado]}>{estado === "PENDIENTE" ? "Pendiente" : estado === "RECHAZADO" ? "Rechazada" : "Deshabilitada"}</Etiqueta>}
          </div>
        );
        // El domiciliario deshabilitado entra a un panel reducido: carné para devolver paquetes y cobros pendientes.
        const abre = estado === "APROBADO" || (rol === "DOMICILIARIO" && estado === "DESHABILITADO");
        return abre ? (
          <Link key={m.conjuntoId + rol} to={rutaDelRol(m.conjuntoId, rol)} className="tarjeta tocable" style={{ textDecoration: "none", color: "inherit" }}>
            {contenido}
          </Link>
        ) : (
          <Tarjeta key={m.conjuntoId + rol}>{contenido}</Tarjeta>
        );
      })}

      <Link to="/unirme" className="boton boton-secundario boton-grande">
        <Icono nombre="mas" /> Unirme a un conjunto
      </Link>
      <AvisoInstalar />
      {todos.some((r) => r.estado === "RECHAZADO") && (
        <p className="chico texto-suave centro">Si te rechazaron por un error, corrige tus datos y vuelve a solicitar desde “Unirme a un conjunto”.</p>
      )}
      <Boton variante="texto" onClick={() => location.reload()}>
        Actualizar
      </Boton>
    </Pantalla>
  );
}
